import React, { useEffect, useMemo, useState } from "react";
import { Wallet, Coins, Briefcase, Target, Sprout, House, ShoppingBag, Trees, MoreHorizontal, ChevronRight, BadgeCheck } from "lucide-react";
import { api } from "../../lib/api";
import { jamWIB } from "../../lib/sky";
import { C, FONT, fmtWaktu } from "./ui";
import { Sheet, Empty } from "./Sheet";
import { TOKEN_KEY, readProfile, Verifikasi } from "./PesanSheet";
import { KartuProfesi } from "./KartuProfesi";

// Kategori ringkasan buku besar: [key, label, ikon, warna, jenis-ledger yang masuk] — null = sisanya (Lainnya)
const KATEGORI = [
  ["kerja", "Kerja", Briefcase, "#e9731c", ["kerja"]],
  ["misi", "Misi", Target, "#7c3aed", ["misi"]],
  ["kebun", "Kebun", Sprout, "#2e9e5b", ["rumah_panen", "rumah_bibit", "rumah_kebun"]],
  ["rumah", "Rumah", House, "#2563eb", ["rumah_dekor", "rumah_hias"]],
  ["belanja", "Mall & Toko", ShoppingBag, "#d9467a", ["mall", "toko"]],
  ["alun", "Alun-alun", Trees, "#0f9ba8", ["alun"]],
  ["lain", "Lainnya", MoreHorizontal, "#6b7280", null],
];
const HIJAU = "#189a5b";
const fmt = n => (n > 0 ? `+${n}` : `${n}`).replace("-", "−");
const warnaNilai = n => (n > 0 ? HIJAU : n < 0 ? C.danger : C.sub);

// Ringkasan per kategori untuk satu periode + daftar transaksinya
function ringkas(riwayat, sejakMs) {
  const dalam = riwayat.filter(r => new Date(r.created_at).getTime() >= sejakMs);
  const dikenal = new Set(KATEGORI.flatMap(k => k[4] || []));
  const baris = KATEGORI.map(([key, label, Icon, warna, jenis]) => {
    const tx = dalam.filter(r => (jenis ? jenis.includes(r.jenis) : !dikenal.has(r.jenis)));
    return { key, label, Icon, warna, tx, total: tx.reduce((a, r) => a + r.jumlah, 0) };
  });
  return { baris, total: dalam.reduce((a, r) => a + r.jumlah, 0), jumlah: dalam.length };
}

// Baris kategori: ikon berwarna · label · nilai (+hijau / −merah) · chevron → buka daftar transaksi kategori itu
function BarisKategori({ b, buka, onToggle }) {
  return (
    <div style={{ borderTop: `1px solid ${C.line}` }}>
      <button data-testid={`dompet-kat-${b.key}`} aria-expanded={buka} onClick={onToggle} disabled={!b.tx.length}
        style={{ display: "flex", alignItems: "center", gap: 12, width: "100%", padding: "11px 2px", border: 0, background: "transparent", cursor: b.tx.length ? "pointer" : "default", fontFamily: FONT, color: C.ink, textAlign: "left" }}>
        <span style={{ width: 36, height: 36, borderRadius: 99, background: b.warna, color: "#fff", display: "grid", placeItems: "center", flex: "none" }}><b.Icon size={16} strokeWidth={2.2} /></span>
        <b style={{ flex: 1, fontSize: 14 }}>{b.label}</b>
        <b data-testid={`dompet-kat-${b.key}-nilai`} style={{ fontSize: 14, color: warnaNilai(b.total), fontVariantNumeric: "tabular-nums" }}>{b.total === 0 ? "0" : fmt(b.total)}</b>
        <ChevronRight size={16} color={b.tx.length ? C.sub : C.line} style={{ transform: buka ? "rotate(90deg)" : "none", transition: "transform .18s" }} />
      </button>
      {buka && (
        <div data-testid={`dompet-kat-${b.key}-rincian`} style={{ margin: "0 0 10px 48px", borderLeft: `2px solid ${C.line}`, paddingLeft: 12 }}>
          {b.tx.map(r => (
            <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0" }}>
              <span style={{ flex: 1, minWidth: 0 }}><span style={{ display: "block", fontSize: 12.5, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.keterangan}</span><span style={{ fontSize: 11, color: C.sub }}>{fmtWaktu(r.created_at)}</span></span>
              <b style={{ fontSize: 12.5, color: warnaNilai(r.jumlah), fontVariantNumeric: "tabular-nums" }}>{fmt(r.jumlah)}</b>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Sheet Dompet (ala referensi): saldo besar + sub profesi, toggle Hari ini | 7 hari, ringkasan per kategori (ketuk → rincian),
// baris Total, lalu kartu profesi aktif (→ Kerja). Hanya pemegang Kartu Warga.
export function DompetSheet({ onClose, onKerja, cityId }) {
  const [token] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [d, setD] = useState(null);
  const [verif, setVerif] = useState(false);
  const [periode, setPeriode] = useState("hari");
  const [buka, setBuka] = useState(null);
  const warga = readProfile();
  useEffect(() => { if (token) api.ekonomiDompet(token).then(setD).catch(() => setD(null)); }, [token]);

  const sejak = useMemo(() => (periode === "hari" ? Date.now() - jamWIB() * 3600000 : Date.now() - 7 * 86400000), [periode]);
  const r = useMemo(() => ringkas(d?.riwayat_7hari || [], sejak), [d, sejak]);

  if (verif) return <Verifikasi cityId={cityId} onBack={() => setVerif(false)} onClose={onClose} />;
  const aktif = d?.profesi_aktif ? d.profesi[d.profesi_aktif] : null;
  const seg = (k, label) => <button key={k} data-testid={`dompet-periode-${k}`} aria-pressed={periode === k} onClick={() => { setPeriode(k); setBuka(null); }} style={{ flex: 1, height: 36, borderRadius: 99, border: 0, background: periode === k ? "#fff" : "transparent", color: periode === k ? C.ink : C.sub, fontWeight: 800, fontSize: 13, cursor: "pointer", fontFamily: FONT, boxShadow: periode === k ? "0 2px 8px rgba(20,20,20,.08)" : "none" }}>{label}</button>;

  return (
    <Sheet testid="dompet-sheet" title="Dompet" sub={warga ? `${warga.nama} · @${warga.ig_username}` : "Koin Jawsel milikmu"} icon={Wallet} onClose={onClose}>
      {!token && (
        <div style={{ textAlign: "center" }}>
          <Empty icon={Wallet} text="Dompet hanya untuk pemegang Kartu Warga. Aktifkan kartumu, lalu bekerja untuk mengumpulkan koin." />
          <button data-testid="dompet-jadi-warga" onClick={() => setVerif(true)} style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, width: "100%", padding: "13px 18px", border: 0, borderRadius: 99, background: C.blue, color: "#fff", fontSize: 14, fontWeight: 800, cursor: "pointer", fontFamily: FONT }}><BadgeCheck size={17} /> Jadi Warga Jawsel</button>
        </div>
      )}
      {token && (
        <>
          <div data-testid="dompet-saldo" style={{ display: "flex", alignItems: "center", gap: 14, padding: "4px 2px 14px" }}>
            <span style={{ width: 50, height: 50, borderRadius: 99, background: "#f4b400", color: "#141414", display: "grid", placeItems: "center", flex: "none", boxShadow: "0 6px 16px rgba(244,180,0,.35)" }}><Coins size={24} strokeWidth={2.2} /></span>
            <span style={{ minWidth: 0 }}>
              <b data-testid="dompet-saldo-nilai" style={{ display: "block", fontSize: 32, letterSpacing: -1, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>{d ? d.saldo : "…"}</b>
              <span data-testid="dompet-saldo-sub" style={{ display: "block", fontSize: 12.5, color: C.sub, fontWeight: 600, marginTop: 4 }}>Saldo · {aktif ? `${aktif.nama} Lv ${aktif.level} · ×${(aktif.pengali || 1).toFixed(2)} koin` : d ? "belum punya profesi" : "memuat…"}</span>
            </span>
          </div>
          <div data-testid="dompet-periode" style={{ display: "flex", gap: 2, padding: 4, background: C.cream, borderRadius: 99 }}>{seg("hari", "Hari ini")}{seg("7hari", "7 hari")}</div>

          <div data-testid="dompet-ringkasan" style={{ marginTop: 10 }}>
            {r.baris.map(b => <BarisKategori key={b.key} b={b} buka={buka === b.key} onToggle={() => setBuka(buka === b.key ? null : b.key)} />)}
            <div data-testid="dompet-total" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "13px 2px 4px", borderTop: `1px solid ${C.line}` }}>
              <b style={{ fontSize: 14 }}>Total</b>
              <b data-testid="dompet-total-nilai" style={{ fontSize: 15, color: warnaNilai(r.total), fontVariantNumeric: "tabular-nums" }}>{r.total === 0 ? "0" : fmt(r.total)}</b>
            </div>
            {d && r.jumlah === 0 && <p data-testid="dompet-kosong" style={{ margin: "6px 2px 0", fontSize: 12, color: C.sub }}>Belum ada transaksi {periode === "hari" ? "hari ini" : "7 hari terakhir"}. Mulai shift di menu Kerja untuk mengumpulkan koin.</p>}
          </div>

          <p style={{ margin: "16px 2px 8px", fontSize: 11, fontWeight: 800, letterSpacing: 1.4, textTransform: "uppercase", color: C.sub }}>Profesi aktif</p>
          {aktif
            ? <KartuProfesi p={{ key: aktif.profesi, nama: aktif.nama, deskripsi: `${aktif.shifts} shift dikerjakan` }} data={aktif} aktif utama={d.profesi_utama === aktif.profesi} onClick={onKerja} compact />
            : <button data-testid="dompet-pilih-profesi" onClick={onKerja} style={{ display: "flex", alignItems: "center", gap: 12, width: "100%", textAlign: "left", border: 0, padding: "12px 14px", background: C.cream, borderRadius: 18, cursor: "pointer", color: C.ink, fontFamily: FONT }}>
                <span style={{ width: 42, height: 42, borderRadius: 14, background: "#fff", display: "grid", placeItems: "center" }}><Briefcase size={20} color={C.blue} /></span>
                <span style={{ flex: 1 }}><b style={{ fontSize: 14.5 }}>Belum punya profesi</b><span style={{ display: "block", fontSize: 11.5, color: C.sub }}>Pilih Penebang, Petani, Tukang, Barista, atau Sopir di menu Kerja</span></span>
                <ChevronRight size={16} color={C.sub} />
              </button>}
        </>
      )}
    </Sheet>
  );
}
